import * as THREE from 'three'
import type { CameraMode, GameModeId, GameHudView, MapManifest, PlayPhase, RoundResult } from './types'
import { EventBus } from './EventBus'
import { INTERACT_RANGE } from './const'
import {
  generateLayout,
  pickMineCount,
  slotCoveringPosition,
  frontRowSlotIds,
  virtualEntranceMineCount,
  inEntranceScanZone,
  shouldExplodeOnStep,
  DefuseSM,
} from './mines'
import type { MineFieldGraph } from './mines'
import { InputManager } from '@/input/InputManager'
import { FpsController } from '@/player/FpsController'
import { MapRuntime } from '@/scene/MapRuntime'
import { getBest, saveWin } from '@/persistence/records'
import type { PlayerSettings } from '@/persistence/settings'

export interface GameEvents {
  hud: GameHudView
  won: RoundResult
  lost: RoundResult
  pointerLockLost: boolean
}

function hudSignature(view: GameHudView): string {
  const t = Math.floor(view.timerMs / 100)
  return [
    view.phase,
    view.mode,
    view.mapId,
    t,
    view.defuse.active ? '1' : '0',
    view.defuse.slotId ?? '',
    Math.floor(view.defuse.t * 20),
    view.defusedCount,
    view.activeMines,
    view.mineCount,
    view.safeRevealed,
    view.safeTotal,
    view.progressLabel,
    view.adjacentCount === null ? 'n' : String(view.adjacentCount),
    view.cameraMode,
    view.hint ?? '',
    view.pointerLockLost ? '1' : '0',
  ].join('|')
}

export class GameEngine {
  readonly bus = new EventBus<GameEvents>()
  private renderer: THREE.WebGLRenderer
  private scene = new THREE.Scene()
  private player: FpsController
  private input: InputManager
  private mapRuntime: MapRuntime | null = null
  private graph: MineFieldGraph | null = null
  private defuseSm = new DefuseSM()
  private raf = 0
  private lastTs = 0
  private phase: PlayPhase = 'loading'
  private mode: GameModeId = 'exit'
  private manifest: MapManifest | null = null
  private timerMs = 0
  private seed = 0
  private mineCount = 0
  private aimSlot: string | null = null
  private adjacentCount: number | null = null
  private hint: string | null = null
  private hintTtl = 0
  private settings: PlayerSettings
  private cameraMode: CameraMode = 'fps'
  private disposed = false
  private lastHudSig = ''
  private onPointerLockChange = () => {
    if (this.phase !== 'playing') return
    if (document.pointerLockElement !== this.canvas && !this.input.isTouch) {
      this.phase = 'paused'
      this.input.setEnabled(false)
      this.bus.emit('pointerLockLost', true)
      this.emitHud(true)
    }
  }

  constructor(
    private canvas: HTMLCanvasElement,
    settings: PlayerSettings,
  ) {
    this.settings = settings
    this.cameraMode = settings.cameraMode
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight, false)
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    this.scene.background = new THREE.Color(0x7a848c)
    this.scene.fog = new THREE.Fog(0x7a848c, 18, 55)

    const hemi = new THREE.HemisphereLight(0x8a949c, 0x3a3f44, 0.85)
    this.scene.add(hemi)
    const key = new THREE.DirectionalLight(0xfff2dd, 0.9)
    key.position.set(8, 16, 4)
    key.castShadow = true
    this.scene.add(key)
    const fill = new THREE.DirectionalLight(0x6e7a84, 0.35)
    fill.position.set(-6, 8, -4)
    this.scene.add(fill)

    this.player = new FpsController(canvas.clientWidth / Math.max(1, canvas.clientHeight))
    this.player.setLookSettings(settings.lookSensitivity, settings.invertY)
    this.player.setCameraMode(this.cameraMode)
    this.scene.add(this.player.body)
    this.input = new InputManager(canvas, settings.preferTouch)

    document.addEventListener('pointerlockchange', this.onPointerLockChange)
    window.addEventListener('resize', this.onResize)

    this.ensureLoop()
  }

  get isTouch(): boolean {
    return this.input.isTouch
  }

  get mobile() {
    return this.input.mobile
  }

  updateSettings(settings: PlayerSettings): void {
    this.settings = settings
    this.player.setLookSettings(settings.lookSensitivity, settings.invertY)
    this.input.setTouchMode(
      settings.preferTouch !== null
        ? settings.preferTouch
        : this.input.isTouch,
    )
    if (this.phase !== 'playing') {
      this.cameraMode = settings.cameraMode
      this.player.setCameraMode(this.cameraMode)
    }
  }

  private onResize = () => {
    const w = this.canvas.clientWidth
    const h = this.canvas.clientHeight
    this.renderer.setSize(w, h, false)
    this.player.camera.aspect = w / Math.max(1, h)
    this.player.camera.updateProjectionMatrix()
  }

  private ensureLoop(): void {
    if (this.disposed || this.raf) return
    this.lastTs = performance.now()
    this.raf = requestAnimationFrame(this.loop)
  }

  startRound(manifest: MapManifest, mode: GameModeId, seed = Date.now()): void {
    this.teardownMap()
    this.manifest = manifest
    this.mode = mode
    this.seed = seed
    this.timerMs = 0
    this.phase = 'loading'
    this.aimSlot = null
    this.adjacentCount = null
    this.lastHudSig = ''
    this.defuseSm.reset()
    this.cameraMode = this.settings.cameraMode

    this.hint = this.settings.showControlsHints
      ? 'Шаг = вскрытие; F/Check — прицел; hold Defuse; мина — поражение'
      : null
    this.hintTtl = this.hint ? 5 : 0

    this.mapRuntime = new MapRuntime(manifest)
    this.mapRuntime.setExitVisible(mode === 'exit')
    this.scene.add(this.mapRuntime.root)

    this.player.resetFromMap(manifest, this.cameraMode)
    const nearest = this.nearestSlotId()
    const count = pickMineCount(manifest.mineCountRange.min, manifest.mineCountRange.max, seed)
    this.mineCount = count
    const gen = generateLayout(manifest.slots, count, seed, {
      safeStartId: nearest ?? undefined,
      tileSize: manifest.tileSize,
    })
    this.graph = gen.graph

    this.phase = 'playing'
    this.input.setEnabled(true)
    if (!this.input.isTouch) this.input.desktop.requestPointerLock()
    this.lastTs = performance.now()
    this.updateScannerReading()
    this.emitHud(true)
    this.ensureLoop()
  }

  private nearestSlotId(): string | null {
    if (!this.manifest) return null
    let best: string | null = null
    let bestD = Infinity
    for (const s of this.manifest.slots) {
      const dx = s.position.x - this.player.position.x
      const dz = s.position.z - this.player.position.z
      const d = dx * dx + dz * dz
      if (d < bestD) {
        bestD = d
        best = s.id
      }
    }
    return best
  }

  pause(): void {
    if (this.phase !== 'playing') return
    this.phase = 'paused'
    this.input.setEnabled(false)
    if (document.pointerLockElement) document.exitPointerLock()
    this.emitHud(true)
  }

  resume(): void {
    if (this.phase !== 'paused') return
    this.phase = 'playing'
    this.input.setEnabled(true)
    if (!this.input.isTouch) this.input.desktop.requestPointerLock()
    this.lastTs = performance.now()
    this.emitHud(true)
  }

  private loop = () => {
    if (this.disposed) return
    this.raf = requestAnimationFrame(this.loop)
    const now = performance.now()
    let dt = (now - this.lastTs) / 1000
    this.lastTs = now
    dt = Math.min(dt, 0.05)

    if (this.phase === 'playing') {
      this.tick(dt)
    }
    this.renderer.render(this.scene, this.player.camera)
  }

  private tick(dt: number): void {
    if (!this.graph || !this.mapRuntime || !this.manifest) return

    const input = this.input.sample(dt)
    if (input.pause) {
      this.pause()
      return
    }

    if (input.cameraToggle) {
      this.cameraMode = this.cameraMode === 'fps' ? 'tpp' : 'fps'
      this.player.setCameraMode(this.cameraMode)
    }

    this.player.integrate(dt, input)
    this.timerMs += dt * 1000
    if (this.hintTtl > 0) {
      this.hintTtl -= dt
      if (this.hintTtl <= 0) this.hint = null
    }

    const ray = this.player.getLookRay()
    this.aimSlot = this.mapRuntime.raycastSlot(ray, INTERACT_RANGE)
    this.mapRuntime.setHover(this.aimSlot)

    if (this.resolveStepReveal()) return

    // Check после feet, до DefuseSM; ignore во время channel
    if (input.check && !this.defuseSm.progress.active) {
      if (this.resolveCheckAim()) return
    }

    this.updateScannerReading()

    const defuseResult = this.defuseSm.tick(dt, input.defuseHold, this.aimSlot, this.graph)
    if (defuseResult.kind === 'complete') {
      const slot = this.graph.getSlot(defuseResult.slotId)!
      this.mapRuntime.syncSlot(defuseResult.slotId, slot.state)
      if (defuseResult.result === 'defused') {
        const mine = this.graph.getMine(defuseResult.slotId)
        const def = this.manifest.slots.find((s) => s.id === defuseResult.slotId)
        if (mine && def) {
          this.mapRuntime.syncDefusedMine(mine, { x: def.position.x, z: def.position.z })
        }
        const count = this.graph.adjacentMineCount(defuseResult.slotId)
        this.mapRuntime.bakeAdjacent(defuseResult.slotId, count)
        this.updateScannerReading()
      } else if (defuseResult.result === 'empty') {
        this.hint = 'Пусто — дольше'
        this.hintTtl = 1.8
      }
      this.mapRuntime.setChannelCue(null, 0)
    } else if (defuseResult.kind === 'progress') {
      this.mapRuntime.setChannelCue(defuseResult.slotId, defuseResult.t)
    } else {
      this.mapRuntime.setChannelCue(null, 0)
    }

    if (this.mode === 'clear') {
      if (this.graph.allMinesDefused()) {
        this.finish(true, 'Поле очищено')
        return
      }
    } else {
      const ez = this.manifest.exitZone
      const dx = this.player.position.x - ez.center.x
      const dz = this.player.position.z - ez.center.z
      if (dx * dx + dz * dz <= ez.radius * ez.radius) {
        this.finish(true, 'Выход достигнут')
        return
      }
    }

    this.emitHud()
  }

  private resolveStepReveal(): boolean {
    if (!this.graph || !this.mapRuntime || !this.manifest) return false
    const slotId = slotCoveringPosition(
      this.manifest.slots,
      this.player.position.x,
      this.player.position.z,
      this.manifest.tileSize,
    )
    if (!slotId) return false

    const slot = this.graph.getSlot(slotId)
    if (shouldExplodeOnStep(slot)) {
      const result = this.graph.revealFromStep(slotId)
      if (result.kind === 'exploded') {
        this.mapRuntime.syncSlot(slotId, 'mineExploded')
        this.finish(false, 'Мина')
        return true
      }
    }

    if (slot && !slot.hasMine && slot.state === 'unknown') {
      const result = this.graph.revealFromStep(slotId)
      if (result.kind === 'revealed') {
        this.mapRuntime.syncSlot(slotId, 'safeRevealed', result.count)
        this.mapRuntime.bakeAdjacent(slotId, result.count)
      }
    }

    if (slot && slot.state === 'defused') {
      const count = this.graph.adjacentMineCount(slotId)
      this.mapRuntime.bakeAdjacent(slotId, count)
    }

    return false
  }

  /** Aim-reveal: та же state-machine что step, без перемещения. */
  private resolveCheckAim(): boolean {
    if (!this.graph || !this.mapRuntime || !this.aimSlot) return false

    const result = this.graph.revealFromStep(this.aimSlot)
    if (result.kind === 'exploded') {
      this.mapRuntime.syncSlot(this.aimSlot, 'mineExploded')
      this.finish(false, 'Мина')
      return true
    }
    if (result.kind === 'revealed') {
      this.mapRuntime.syncSlot(this.aimSlot, 'safeRevealed', result.count)
      this.mapRuntime.bakeAdjacent(this.aimSlot, result.count)
    }
    return false
  }

  private updateScannerReading(): void {
    if (!this.graph || !this.manifest) {
      this.adjacentCount = null
      return
    }

    const tile = this.manifest.tileSize
    const feetSlot = slotCoveringPosition(
      this.manifest.slots,
      this.player.position.x,
      this.player.position.z,
      tile,
    )

    if (feetSlot) {
      this.adjacentCount = this.graph.adjacentMineCount(feetSlot)
      return
    }

    if (
      inEntranceScanZone(
        this.manifest.slots,
        this.player.position.x,
        this.player.position.z,
        tile,
      )
    ) {
      const front = frontRowSlotIds(this.manifest.slots)
      this.adjacentCount = virtualEntranceMineCount(this.graph, front)
      return
    }

    this.adjacentCount = null
  }

  private finish(won: boolean, reason: string): void {
    this.phase = won ? 'won' : 'dying'
    this.input.setEnabled(false)
    this.defuseSm.reset()
    if (document.pointerLockElement) document.exitPointerLock()

    let isNewRecord = false
    let previousBestMs: number | null = getBest(this.manifest!.id, this.mode)?.bestMs ?? null
    if (won) {
      const saved = saveWin(this.manifest!.id, this.mode, this.timerMs, this.seed)
      isNewRecord = saved.isNewRecord
      previousBestMs = saved.previousBestMs
    }

    const result: RoundResult = {
      won,
      reason,
      timeMs: this.timerMs,
      mapId: this.manifest!.id,
      mapName: this.manifest!.displayName,
      mode: this.mode,
      isNewRecord,
      previousBestMs,
    }

    this.emitHud(true)
    if (won) this.bus.emit('won', result)
    else this.bus.emit('lost', result)
  }

  private emitHud(force = false): void {
    if (!this.graph || !this.manifest) return
    const safeRevealed = this.graph.safeRevealedCount()
    const safeTotal = this.graph.safeTotal()
    const defused = this.graph.defusedCount()
    const active = Math.max(0, this.mineCount - defused)
    const progressLabel =
      this.mode === 'clear' ? `A${active} D${defused}` : defused > 0 ? `D${defused}` : ''

    const view: GameHudView = {
      phase: this.phase,
      mode: this.mode,
      mapId: this.manifest.id,
      mapName: this.manifest.displayName,
      timerMs: this.timerMs,
      defuse: this.defuseSm.progress,
      defusedCount: defused,
      // Clear: ACT всегда; Exit B1: ACT=0 (анти-спойлер)
      activeMines: this.mode === 'clear' ? active : 0,
      mineCount: this.mode === 'clear' ? this.mineCount : 0,
      safeRevealed,
      safeTotal,
      progressLabel,
      adjacentCount: this.adjacentCount,
      cameraMode: this.cameraMode,
      hint: this.hint,
      pointerLockLost: this.phase === 'paused' && !this.input.isTouch,
    }

    const sig = hudSignature(view)
    if (!force && sig === this.lastHudSig) return
    this.lastHudSig = sig
    this.bus.emit('hud', view)
  }

  private teardownMap(): void {
    if (this.mapRuntime) {
      this.scene.remove(this.mapRuntime.root)
      this.mapRuntime.dispose()
      this.mapRuntime = null
    }
    this.graph = null
    this.defuseSm.reset()
  }

  dispose(): void {
    this.disposed = true
    cancelAnimationFrame(this.raf)
    this.raf = 0
    document.removeEventListener('pointerlockchange', this.onPointerLockChange)
    window.removeEventListener('resize', this.onResize)
    this.teardownMap()
    this.scene.remove(this.player.body)
    this.input.dispose()
    this.renderer.dispose()
    this.bus.clear()
  }
}
